import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flame, Trophy, Share2, Check, Crown, Skull, Target, Swords, Hash, Plus, X, Search } from 'lucide-react';
import { api, parseApiError, type ClanScopedRole } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { accentVarsFor } from '../components/AccentInjector';
import { DisciplineFlagPanel } from '../components/DisciplineFlagPanel';
import { isAdminRole, clanRoleDisplayName } from '../lib/roles';
import { useEffectiveMe } from '../lib/viewAs';

// ─────────────────────────────────────────────────────────────────────────────
// PlayerCard — the individual's forged artefact.
//
// A personal gamer CV: display name, discord handle, current + past clans,
// K/D, matches played, personal score — all authenticated by GG Anvil and
// shareable as a public URL. The forged plate is the visual primitive.
//
// Routes:
//   /player/me             — logged-in user's own card
//   /player/:discordId     — anyone else's (pulls stats, limited profile)
//
// Today: reads /me for own profile, /players/:id/stats for lifetime + active
// comp stats. Clan history is stubbed pending a backend endpoint (flagged).
// ─────────────────────────────────────────────────────────────────────────────

type PlayerStats = {
  discordId: string;
  scope?: string;
  compId?: string;
  matchesPlayed?: number;
  gamesPlayed?: number;
  totalEliminations?: number;
  totalDeaths?: number;
  overallKd?: number;
  modeStats?: Record<string, { eliminations?: number; deaths?: number; kd?: number }>;
  updatedAt?: number;
};

async function fetchStats(discordId: string, compId?: string): Promise<PlayerStats> {
  return api.playerStats(discordId, compId) as Promise<PlayerStats>;
}

function StatTile({ label, value, sub, highlight = false }: {
  label: string;
  value: string | number;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`plate-sm flex flex-col items-center justify-center gap-1 px-3 py-5 text-center ${highlight ? 'ring-1 ring-forge-500/50' : ''}`}>
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-forge-500/80">
        {label}
      </p>
      <p className={`font-display text-3xl font-black tabular-nums leading-none ${highlight ? 'text-forge-400 drop-shadow-[0_0_12px_rgba(var(--accent-rgb),0.4)]' : 'text-white'}`}>
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-iron-500">
          {sub}
        </p>
      )}
    </div>
  );
}

function Monogram({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  const letter = name?.[0]?.toUpperCase() || '?';
  return (
    <div className="relative flex h-28 w-28 shrink-0 items-center justify-center">
      {/* Glowing hex background */}
      <div className="plate-sm absolute inset-0 ring-1 ring-forge-600/40" />
      <div className="pointer-events-none absolute inset-0 animate-ember-pulse bg-gradient-to-br from-forge-900/20 via-transparent to-forge-900/40" />
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="relative h-full w-full rounded-md object-cover ring-1 ring-forge-500/40 shadow-[0_0_22px_rgba(var(--accent-rgb),0.45)]"
        />
      ) : (
        <span className="relative font-display text-6xl font-black leading-none tracking-tight text-forge-400 drop-shadow-[0_0_16px_rgba(var(--accent-rgb),0.5)]">
          {letter}
        </span>
      )}
    </div>
  );
}

export function PlayerCard() {
  const { discordId: paramId } = useParams<{ discordId: string }>();
  const { activeComp, activeCompId } = useWorkspace();
  const { data: me, isLoading: meLoading } = useQuery({ queryKey: ['me'], queryFn: api.me });

  // Resolve "me" shorthand
  const isMeCard = paramId === 'me';
  const resolvedId = isMeCard ? me?.discordId : paramId;

  // Lifetime stats
  const { data: lifetimeStats } = useQuery({
    queryKey: ['playerStats', resolvedId, 'lifetime'],
    queryFn: () => fetchStats(resolvedId!),
    enabled: !!resolvedId,
    retry: false,
  });

  // Active-comp stats
  const { data: compStats } = useQuery({
    queryKey: ['playerStats', resolvedId, activeCompId],
    queryFn: () => fetchStats(resolvedId!, activeCompId!),
    enabled: !!resolvedId && !!activeCompId,
    retry: false,
  });

  // Clan memberships (current, forged chronologically)
  const { data: clansData, isLoading: clansLoading } = useQuery({
    queryKey: ['playerClans', resolvedId],
    queryFn: () => api.playerClans(resolvedId!),
    enabled: !!resolvedId,
    retry: false,
  });
  const clans = clansData?.clans ?? [];

  // Match history within the active comp — per-match breakdown
  const { data: matchHistory } = useQuery({
    queryKey: ['playerMatchHistory', resolvedId, activeCompId],
    queryFn: () => api.playerMatchHistory(resolvedId!, activeCompId!),
    enabled: !!resolvedId && !!activeCompId,
    retry: false,
  });

  // Public Discord identity (name, avatar, accent) for ANOTHER player's card.
  const { data: profile } = useQuery({
    queryKey: ['playerProfile', resolvedId],
    queryFn: () => api.playerProfile(resolvedId!),
    enabled: !!resolvedId && !isMeCard,
    retry: false,
  });

  const [shareStatus, setShareStatus] = useState<'idle' | 'copied'>('idle');

  // Resolve display info — from /me for self, from the enriched profile for others
  // (so it's their real Discord name + avatar, not an ID-derived placeholder).
  const displayName = isMeCard
    ? me?.displayName
    : (profile?.globalName || profile?.displayName || profile?.gamertag || resolvedId?.slice(-6));
  const handle = isMeCard
    ? `@${me?.displayName?.toLowerCase().replace(/\s/g, '_') ?? 'player'}`
    : (profile?.discordUsername ? `@${profile.discordUsername}` : `#${resolvedId?.slice(-4) ?? '????'}`);
  const role = isMeCard ? me?.role : undefined;

  // Theme this card to the VIEWED player's Discord accent — scoped, so it doesn't
  // touch the app-wide palette. Their avatar + name + colour, matching their
  // Discord. Empty (forge default) when they have no usable accent.
  const cardAccent = (!isMeCard ? accentVarsFor(profile?.accentColor, profile?.accentOverride) : {}) as CSSProperties;

  useEffect(() => {
    if (displayName) document.title = `${displayName} · GG Anvil Player Card`;
  }, [displayName]);

  const onShare = async () => {
    const url = window.location.href.replace('/player/me', `/player/${resolvedId}`);
    try {
      if (navigator.share) {
        await navigator.share({ title: `${displayName} · forged by GG Anvil`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareStatus('copied');
        setTimeout(() => setShareStatus('idle'), 2000);
      }
    } catch {
      /* user cancelled */
    }
  };

  if (meLoading && isMeCard) return <PageSpinner />;

  // Self-vs-other mode toggle (Phase 2 ratification 2026-04-27):
  //   /player/:id viewing yourself → redirect to /me/card (edit mode).
  //   /player/:id viewing someone else → stay here (read-only view mode).
  // Single source of truth per surface; no shared state between view + edit.
  // Both `/player/me` shorthand and `/player/<own-discord-id>` redirect.
  if (me && resolvedId && me.discordId === resolvedId) {
    return <Navigate to="/me/card" replace />;
  }

  if (!resolvedId) {
    return (
      <EmptyState
        title="Player not forged"
        message="No player card exists for this ID yet. Every match played adds to the record."
      />
    );
  }

  // Compute K/D helpers
  const lifeKd = typeof lifetimeStats?.overallKd === 'number' && lifetimeStats.overallKd > 0
    ? lifetimeStats.overallKd.toFixed(2)
    : '—';
  const compKd = typeof compStats?.overallKd === 'number' && compStats.overallKd > 0
    ? compStats.overallKd.toFixed(2)
    : '—';
  const lifeMatches = lifetimeStats?.matchesPlayed ?? 0;
  const compMatches = compStats?.matchesPlayed ?? 0;
  const lifeElims = lifetimeStats?.totalEliminations ?? 0;
  const lifeDeaths = lifetimeStats?.totalDeaths ?? 0;

  const hasLifetimeData = lifeMatches > 0 || lifeElims > 0;

  return (
    <div className="relative" style={cardAccent}>
      {/* Ambient forge glow */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute left-1/2 top-[-20%] h-[50vh] w-[70vw] -translate-x-1/2 rounded-full bg-forge-500/10 blur-[140px]" />
      </div>

      {/* ── Brand strip ─────────────────────────────────────────── */}
      <div className="mx-auto mb-5 flex max-w-2xl items-end justify-between px-1">
        <div className="flex items-center gap-2 opacity-85">
          <img src="/brand/gganvil-mark.svg" alt="" className="h-5 w-5" />
          <span className="font-display text-[10px] font-black uppercase tracking-[0.3em] text-forge-300">
            GG Anvil · Player Card
          </span>
        </div>
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-iron-500">
          № {resolvedId.slice(-6)}
        </span>
      </div>

      {/* ── Main plate ──────────────────────────────────────────── */}
      <article className="plate grain animate-forge-in relative mx-auto max-w-2xl px-6 py-8 sm:px-10 sm:py-10">
        {/* Top seal row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-400/90">
              {activeComp?.name ? `${activeComp.name}` : 'Personal Record'}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-iron-500">
              Forged record · updated {lifetimeStats?.updatedAt ? new Date(lifetimeStats.updatedAt).toLocaleDateString('en-GB') : 'pending first match'}
            </p>
          </div>
          <span className="seal shrink-0">
            <Flame className="h-3 w-3" strokeWidth={3} />
            {isMeCard ? 'You' : 'Verified'}
          </span>
        </div>

        {/* Hero — monogram + name */}
        <div className="mt-8 mb-8 flex items-center gap-6">
          <Monogram name={displayName || '?'} avatarUrl={profile?.avatarUrl} />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-iron-500">
              Player
            </p>
            <h1 className="mt-1 truncate font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
              {displayName || 'Unknown'}
            </h1>
            <p className="mt-2 font-mono text-xs uppercase tracking-widest text-forge-400/80">
              {handle}
            </p>
            {role && (
              <div className="mt-3 inline-flex items-center gap-2 border border-forge-700/50 bg-forge-950/40 px-2.5 py-1">
                <Crown className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                <span className="font-display text-[10px] font-black uppercase tracking-wider text-forge-300">
                  {role}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Current comp stats ──────────────────────────────── */}
        {activeComp && (
          <div className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-500">
                In {activeComp.name}
              </p>
              <span className="font-mono text-[9px] uppercase tracking-widest text-iron-500">
                current workspace
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <StatTile
                label="K/D"
                value={compKd}
                sub={compKd === '—' ? 'pending' : 'this comp'}
                highlight={compKd !== '—'}
              />
              <StatTile
                label="Matches"
                value={compMatches}
                sub="played"
              />
              <StatTile
                label="Score"
                value={compStats?.totalEliminations ?? 0}
                sub="elims"
              />
            </div>
          </div>
        )}

        {/* ── Lifetime stats ──────────────────────────────────── */}
        <div className="mb-8">
          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-500">
            Lifetime
          </p>
          {!hasLifetimeData ? (
            <div className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-4 py-4">
              <Hash className="h-4 w-4 shrink-0 text-forge-500/70" strokeWidth={2.5} />
              <p className="font-mono text-[10px] uppercase tracking-widest text-iron-500">
                Forge is cold — no matches recorded yet. First match starts the record.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label="K/D" value={lifeKd} highlight />
              <StatTile label="Matches" value={lifeMatches} />
              <StatTile label="Elims" value={lifeElims} />
              <StatTile label="Deaths" value={lifeDeaths} />
            </div>
          )}
        </div>

        {/* ── Mode breakdown (if available) ───────────────────── */}
        {lifetimeStats?.modeStats && Object.keys(lifetimeStats.modeStats).length > 0 && (
          <div className="mb-8">
            <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-500">
              By Mode
            </p>
            <div className="space-y-1.5">
              {Object.entries(lifetimeStats.modeStats).map(([mode, s]) => {
                const MODE_LABELS: Record<string, string> = { snd: 'Search & Destroy', hardpoint: 'Hardpoint', overload: 'Overload', control: 'Control' };
                const MODE_ICONS: Record<string, typeof Target> = { snd: Target, hardpoint: Skull, overload: Swords, control: Swords };
                const Icon = MODE_ICONS[mode] ?? Target;
                return (
                  <div
                    key={mode}
                    className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-3 py-2.5"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-forge-500" strokeWidth={2.5} />
                    <span className="font-display text-[11px] font-black uppercase tracking-[0.2em] text-iron-300">
                      {MODE_LABELS[mode] ?? mode}
                    </span>
                    <div className="flex-1" />
                    <div className="flex items-center gap-3 font-mono text-xs font-bold tabular-nums">
                      <span className="text-iron-500">
                        {s.eliminations ?? 0}<span className="text-iron-500">/</span>{s.deaths ?? 0}
                      </span>
                      <span className="text-forge-400">
                        {typeof s.kd === 'number' ? s.kd.toFixed(2) : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Match history (per-match scoreboard within active comp) ─── */}
        {activeComp && matchHistory && matchHistory.length > 0 && (
          <div className="mb-8">
            <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-500">
              Match History · {activeComp.name}
            </p>
            <div className="space-y-1.5">
              {matchHistory.slice(0, 12).map(m => {
                const kd = m.totals.kd || (m.totals.deaths > 0 ? m.totals.kills / m.totals.deaths : m.totals.kills);
                return (
                  <div
                    key={m.matchId}
                    className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-3 py-2.5"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-forge-700/40 bg-forge-950/40 font-display text-xs font-black text-forge-300">
                      R{m.roundNumber}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-[11px] font-black uppercase tracking-[0.2em] text-white">
                        {m.clanName}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-widest text-iron-500">
                        {new Date(m.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 font-mono text-xs font-bold tabular-nums">
                      <span className="text-iron-500">
                        {m.totals.kills}<span className="text-iron-500">/</span>{m.totals.deaths}
                      </span>
                      <span className="min-w-[3ch] text-right text-forge-400">
                        {kd.toFixed(2)}
                      </span>
                      <span className="min-w-[5ch] text-right text-green-400">
                        {m.totals.score.toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}
              {matchHistory.length > 12 && (
                <p className="pl-3 pt-2 font-mono text-[9px] uppercase tracking-widest text-iron-500">
                  + {matchHistory.length - 12} earlier matches
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Clan history ────────────────────────────────────── */}
        <div className="mb-8">
          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-forge-500">
            Clan Roster
          </p>
          {clansLoading ? (
            <div className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-4 py-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-iron-500">Loading…</p>
            </div>
          ) : clans.length === 0 ? (
            isMeCard ? <NoClanCtas /> : (
              <div className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-4 py-4">
                <Trophy className="h-4 w-4 shrink-0 text-forge-500/70" strokeWidth={2.5} />
                <p className="font-mono text-[10px] uppercase tracking-widest text-iron-500">
                  No clan memberships yet.
                </p>
              </div>
            )
          ) : (
            <div className="space-y-1.5">
              {clans.map(clan => {
                const isOwner = clan.role === 'CLAN_OWNER';
                const isLeader = clan.role === 'CLAN_LEADER' || isOwner;
                return (
                  <div
                    key={clan.clanName}
                    className="flex items-center gap-3 border-l-2 border-forge-700/60 bg-iron-950 px-4 py-3 transition-colors hover:border-forge-500"
                  >
                    {isLeader ? (
                      <Crown className="h-4 w-4 shrink-0 text-forge-400" strokeWidth={2.5} />
                    ) : (
                      <Trophy className="h-4 w-4 shrink-0 text-forge-500/70" strokeWidth={2.5} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-sm font-black uppercase tracking-wider text-white">
                        {clan.clanName}
                      </p>
                      {clan.gamertag && (
                        <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-widest text-iron-500">
                          {clan.gamertag}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 border border-forge-700/50 bg-forge-950/40 px-2 py-1 font-display text-[9px] font-black uppercase tracking-wider text-forge-300">
                      {clanRoleDisplayName(clan.role as ClanScopedRole) || clan.role}
                    </span>
                    {clan.joinedAt > 0 && (
                      <span className="hidden shrink-0 font-mono text-[9px] uppercase tracking-widest text-iron-500 sm:inline">
                        {new Date(clan.joinedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Bottom verification strip ───────────────────────── */}
        <div className="flex items-center justify-between border-t border-forge-900/60 pt-5">
          <div className="flex items-center gap-3">
            <img src="/brand/gganvil-mark.svg" alt="" className="h-8 w-8 animate-anvil-strike" />
            <div className="leading-none">
              <p className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-white">
                Authenticated
              </p>
              <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-iron-500">
                GG Anvil Forge · Player Record
              </p>
            </div>
          </div>

          <button
            onClick={onShare}
            className="flex items-center gap-2 border border-forge-700/50 px-3 py-2 font-display text-[10px] font-black uppercase tracking-[0.2em] text-forge-300 transition-colors hover:border-forge-500 hover:bg-forge-900/40"
          >
            {shareStatus === 'copied' ? (
              <>
                <Check className="h-3 w-3" strokeWidth={3} />
                Copied
              </>
            ) : (
              <>
                <Share2 className="h-3 w-3" strokeWidth={3} />
                Share
              </>
            )}
          </button>
        </div>
      </article>

      {/* ── Discipline (admin only) ─────────────────────────────── */}
      {isAdminRole(me?.role) && resolvedId && (
        <div className="mx-auto mt-6 max-w-2xl">
          <DisciplineFlagPanel
            targetType="player"
            targetId={resolvedId}
            targetName={displayName}
            orgId={null}
          />
        </div>
      )}

      {/* Footer note */}
      <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-iron-500">
        Edit mode coming soon · bio · favourite mode · avatar
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// NoClanCtas — shown on the user's own card when they have no clan yet.
// Two paths: join a clan (browse) or create a clan.
//
// Create is ROLE-AWARE (Lisa, 2026-07-19): an org member without admin power
// must always see "Request to create a clan" and land in the APPLICATION flow
// (BuildClanScreen's apply branch) — this card is exactly where a member who
// entered through the VIEWING gate discovers clan creation from inside the
// app, so it must never offer the direct-forge modal their role can't use
// (the backend 403s it since the member-structure epic). Founders/platform
// admins keep the direct modal. Same org-context test BuildClanScreen
// applies: any org membership, or the entry-org claim on the session.
// ─────────────────────────────────────────────────────────────────────────────

function NoClanCtas() {
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const effectiveMe = useEffectiveMe(me);
  const isPrivileged = effectiveMe?.role === 'founder' || effectiveMe?.role === 'admin';
  const hasOrgContext = (effectiveMe?.orgMemberships?.length ?? 0) > 0 || !!effectiveMe?.entryOrgId;
  const applyMode = hasOrgContext && !isPrivileged;
  return (
    <>
      <div className="border-l-2 border-forge-500/60 bg-forge-900/20 p-5">
        <p className="mb-1 font-display text-sm font-black uppercase tracking-wider text-white">
          No clan yet
        </p>
        <p className="mb-4 font-mono text-[11px] uppercase tracking-wider text-iron-400">
          Find an existing clan to join, or start your own and lead it.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/clans/browse"
            className="inline-flex items-center gap-2 border border-forge-700/60 bg-iron-950 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-iron-200 transition-colors hover:border-forge-500/60 hover:text-forge-200"
          >
            <Search className="h-3.5 w-3.5" strokeWidth={2.5} /> Find a clan
          </Link>
          <button
            onClick={() => (applyMode ? navigate('/gate/build-clan') : setCreateOpen(true))}
            className="ember inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> {applyMode ? 'Request to create a clan' : 'Create a clan'}
          </button>
        </div>
      </div>
      {createOpen && <CreateClanModal onClose={() => setCreateOpen(false)} />}
    </>
  );
}

function CreateClanModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [gamertag, setGamertag] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // W3 soft-stamp: bind a bare clan create to the active workspace org.
  const { activeOrgId } = useWorkspace();

  const create = useMutation({
    mutationFn: () => api.createClan({
      name: name.trim(),
      description: description.trim() || undefined,
      ...(gamertag.trim() ? { gamertag: gamertag.trim() } : {}),
      orgId: activeOrgId ?? undefined,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['playerClans'] });
      onClose();
      // Drop the user into their new clan's leader panel.
      navigate('/leader');
      // Avoid unused var warning on res
      void res;
    },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed to create clan.'),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const valid = name.trim().length >= 2 && !create.isPending;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setErr(null); if (valid) create.mutate(); }}
          className="w-full max-w-md border border-forge-900/40 bg-iron-950 p-5 shadow-2xl"
        >
          <div className="mb-3 flex items-start justify-between">
            <h3 className="font-display text-lg font-black uppercase tracking-wider text-white">
              Create a clan
            </h3>
            <button type="button" onClick={onClose} className="text-iron-500 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-4 font-mono text-xs text-iron-400">
            You become the <span className="text-emerald-300">Leader</span>. Name must be unique.
          </p>

          <label className="mb-3 block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-iron-500">
              Clan name
            </span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Iron Vanguard"
              maxLength={50}
              className="input"
              autoFocus
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-iron-500">
              Description (optional)
            </span>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's your clan about?"
              maxLength={200}
              className="input"
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-iron-500">
              Your gamertag (optional)
            </span>
            <input
              value={gamertag}
              onChange={e => setGamertag(e.target.value)}
              placeholder="e.g. YourName#1234"
              maxLength={50}
              className="input"
            />
          </label>

          {err && <p className="mb-2 font-mono text-xs text-red-400">{err}</p>}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-forge-900/60 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-iron-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!valid}
              className="ember flex-1 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40 disabled:opacity-50"
            >
              {create.isPending ? 'Forging…' : 'Create clan'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
